import { useUserPermissions } from "@bebit/auth";
import { i18n } from "@bebit/store";
import { FavoriteSearch } from "search/favoriteSearch";
import { computed, ComputedRef, Ref } from "vue";
import { Category } from "@host/types/side-bar/SideBar.type";
import { useFavoriteSearch } from "./useFavoriteSearch";
import type { Icon } from "@bebit/atomic";

export function useSideBar() {
  const { favoriteSearches, setFavoriteSearches } = useFavoriteSearch();
  const { isGuestUser, hasTourPermission } = useUserPermissions();
  const { behaviorAnalysisCategory } = useBehaviorAnalysisCategory();
  const { myDataCategory } = useMyDataCategory(favoriteSearches);
  const { thematicAnalysisCategory } = useThematicAnalysisCategory();

  const categories = computed<Category[]>(() => {
    if (isGuestUser.value) return [myDataCategory.value];
    if (!hasTourPermission.value) return [behaviorAnalysisCategory, myDataCategory.value];
    return [behaviorAnalysisCategory, thematicAnalysisCategory, myDataCategory.value];
  });

  return { categories, setFavoriteSearches };
}

// eslint-disable-next-line max-lines-per-function
function useBehaviorAnalysisCategory() {
  const {
    hasAppAnalysisPermission,
    hasWebAnalysisPermission,
    hasFunnelAnalysisPermission,
    hasJourneyAnalysisPermission,
    hasStatsPermission,
  } = useUserPermissions();

  const behaviorAnalysisCategory: Category = {
    category: i18n.global.t("composables.useSideBar.behaviorAnalysis"),
    items: [
      {
        icon: "horizontal-bars",
        text: i18n.global.t("composables.useSideBar.funnelAnalysis"),
        routeName: "funnel-analysis-home",
        hidden: !hasFunnelAnalysisPermission.value,
      },
      {
        icon: "branches",
        text: i18n.global.t("composables.useSideBar.journeyAnalysis"),
        routeName: "Journey",
        hidden: !hasJourneyAnalysisPermission.value,
      },
      {
        icon: "analysis",
        text: i18n.global.t("composables.useSideBar.accessAnalysis"),
        storageKey: "expandable_analysis",
        subitems: [
          {
            icon: "analysis",
            text: i18n.global.t("composables.useSideBar.pageAnalysis"),
            v1Url: "/user-trend/web-views",
            hidden: !hasWebAnalysisPermission.value,
          },
          {
            icon: "analysis",
            text: i18n.global.t("composables.useSideBar.landingPageAnalysis"),
            v1Url: "/user-trend/landings",
            hidden: !hasWebAnalysisPermission.value,
          },
          {
            icon: "analysis",
            text: i18n.global.t("composables.useSideBar.inflowSourceAnalysis"),
            v1Url: "/user-trend/inflows",
            hidden: !hasWebAnalysisPermission.value,
          },
          {
            icon: "analysis",
            text: i18n.global.t("composables.useSideBar.appPageAnalysis"),
            v1Url: "/user-trend/app-views",
            hidden: !hasAppAnalysisPermission.value,
          },
          {
            icon: "analysis",
            text: i18n.global.t("composables.useSideBar.accessOverview"),
            routeName: "Stats",
            hidden: !hasStatsPermission.value,
          },
        ],
      },
    ],
  };

  return { behaviorAnalysisCategory };
}

function useThematicAnalysisCategory() {
  const { hasTourPermission } = useUserPermissions();

  const thematicAnalysisCategory: Category = {
    category: i18n.global.t("composables.useSideBar.thematicAnalysis"),
    items: [
      {
        icon: "thematic-analysis",
        text: i18n.global.t("composables.useSideBar.issueDiscoveryTour"),
        v1Url: "/tours",
        hidden: !hasTourPermission.value,
      },
    ],
  };

  return { thematicAnalysisCategory };
}

interface FavoriteSearchItem {
  icon: Icon;
  text: string;
  favoriteSearch: FavoriteSearch;
}

// eslint-disable-next-line max-lines-per-function
function useMyDataCategory(favoriteSearches: Ref<FavoriteSearch[]>) {
  const { isGuestUser } = useUserPermissions();

  const favoriteSearchItems: ComputedRef<FavoriteSearchItem[]> = computed(() =>
    favoriteSearches.value.map((searchItem: FavoriteSearch) => ({
      icon: "pin",
      text: searchItem.label,
      favoriteSearch: searchItem,
    }))
  );

  // eslint-disable-next-line max-lines-per-function
  const myDataCategory = computed<Category>(() => ({
    category: i18n.global.t("composables.useSideBar.myData"),
    items: [
      {
        icon: "heart",
        text: i18n.global.t("composables.useSideBar.bookmarkedUsers"),
        v1Url: "/bookmark-users",
      },
      {
        icon: "pin",
        text: i18n.global.t("composables.useSideBar.savedSearchConditions"),
        storageKey: "expandable_pin",
        subitems: favoriteSearchItems.value,
        hidden: isGuestUser.value,
      },
      {
        icon: "history",
        text: i18n.global.t("composables.useSideBar.watchHistory"),
        v1Url: "/watch-history",
      },
      {
        icon: "memo",
        text: i18n.global.t("composables.useSideBar.memoUsers"),
        v1Url: "/memo-users",
      },
    ],
  }));

  return { myDataCategory };
}
